import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  IconButton,
  InlineInputButton,
  Input,
  InputGroup,
  Label,
  Select,
} from "../../EditorStyles";
import { ReactComponent as TrashIcon } from "./../../../../images/editor-icons/trash.svg";
import { ReactComponent as CloseIcon } from "./../../../../images/editor-icons/x.svg";
import { ReactComponent as OpenIcon } from "./../../../../images/editor-icons/external-link.svg";
import { FormattedMessage, useIntl } from "react-intl";
import { useEditState } from "../../EditContext";
import ConfirmDialog from "../../BaseComponents/ConfirmDialog";
import { useRestAuth } from "../../../../hooks/useRestAuth";
import { useRefreshEditorData } from "../../../../hooks/useRefreshEditorData";
import {
  BasicEditorMapPano,
  BasicEditorMapPoi,
  EditorData,
  EditorMapPano,
  EditorMapPoi,
  SelectedLink,
} from "../../../../utils/editorUtils";
import SpinnerWrapper from "../../BaseComponents/SpinnerWrapper";
import { useHistory } from "react-router-dom";
import { useAppState } from "../../../../AppContext";
import MultiSelect from "../../BaseComponents/MultiSelect";

interface MapLinkEditorProps {
  selectedLink: SelectedLink;
  onCloseEditor: () => void;
  setSelectedLink: React.Dispatch<React.SetStateAction<SelectedLink | undefined>>;
  coordinateInputsLoading: boolean;
}

const MapLinkEditor: React.FC<MapLinkEditorProps> = ({
  selectedLink,
  onCloseEditor,
  setSelectedLink,
  coordinateInputsLoading,
}) => {
  const { mapPanoRepository, mapPoiRepository, data, target } = useEditState();
  const restAuth = useRestAuth();
  const history = useHistory();
  const intl = useIntl();
  const [deleteLink, setDeleteLink] = useState(false);
  const { state } = useAppState();
  const [targets, setTargets] = useState<BasicEditorMapPano[] | BasicEditorMapPoi[]>([]);
  const [targetInputLoading, setTargetInputLoading] = useState<boolean>(false);

  const refreshLayerData = useRefreshEditorData();
  
  const [lat, setLatValue] = useState("0");
  const [long, setLongValue] = useState("0");
  const [selectedLayers, setSelectedLayers] = useState<number[]>([]);
  const [layersLoading, setLayersLoading] = useState(false);

  const linkData: EditorMapPano | EditorMapPoi | undefined = useMemo(() => {
    if (selectedLink.type === "pano") {
      return (data as EditorData<"map">).panorama_location.find(
        (location) => location.pano === selectedLink.item
      );
    }
    return (data as EditorData<"map">).poi_location.find(
      (location) => location.poi === selectedLink.item
    );
  }, [selectedLink, data]);

  const getCurrentLayers = useCallback(async () => {
    if (!restAuth || !state.map?.layerGroups || !selectedLink) {
      return [];
    }

    setLayersLoading(true);
    const currentLayers: number[] = [];

    try {
      // Check each layer to see if this point exists in it
      for (const layerGroup of state.map.layerGroups) {
        try {
          let layerData;
          if (selectedLink.type === "pano") {
            layerData = await mapPanoRepository.get(restAuth, layerGroup.id);
          } else {
            layerData = await mapPoiRepository.get(restAuth, layerGroup.id);
          }

          const pointExistsInLayer = layerData.some((point) => {
            if (selectedLink.type === "pano") {
              return (point as EditorMapPano).pano === selectedLink.item;
            } else {
              return (point as EditorMapPoi).poi === selectedLink.item;
            }
          });

          if (pointExistsInLayer) {
            currentLayers.push(layerGroup.id);
          }
        } catch (error) {
          console.warn(`Error checking layer ${layerGroup.id}:`, error);
        }
      }
    } finally {
      setLayersLoading(false);
    }

    return currentLayers;
  }, [
    restAuth,
    state.map?.layerGroups,
    selectedLink,
    mapPanoRepository,
    mapPoiRepository,
    setLayersLoading,
  ]);

  useEffect(() => {
    const initializeLayers = async () => {
      if (linkData) {
        setLatValue(linkData.lat.toString() || "0");
        setLongValue(linkData.long.toString() || "0");

        const currentLayers = await getCurrentLayers();
        setSelectedLayers(currentLayers);
      }
    };

    initializeLayers();
  }, [linkData, getCurrentLayers]);

  useEffect(() => {
    const fetchData = async () => {
      if (restAuth) {
        setTargetInputLoading(true);
        let targets = [] as BasicEditorMapPano[] | BasicEditorMapPoi[];
        if (selectedLink.type === "pano") {
          const all_targets = await mapPanoRepository.get(restAuth);
          const unlinked_targets = await mapPanoRepository.get_unused(restAuth);
          targets = [
            ...unlinked_targets,
            ...all_targets.filter((target) => target.pano === selectedLink.item),
          ];
        } else {
          const all_targets = await mapPoiRepository.get(restAuth);
          const unlinked_targets = await mapPoiRepository.get_unused(restAuth);
          targets = [
            ...unlinked_targets,
            ...all_targets.filter((target) => target.poi === selectedLink.item),
          ];
        }
        setTargets(targets);
        setTargetInputLoading(false);
      }
    };

    fetchData();
  }, [mapPanoRepository, mapPoiRepository, restAuth, selectedLink.type, selectedLink.item]);

  const onDeleteLink = async () => {
    if (restAuth) {
      if (selectedLink.type === "pano") {
        await mapPanoRepository.delete(restAuth, selectedLink.item);
      } else {
        await mapPoiRepository.delete(restAuth, selectedLink.item);
      }

      // Refresh all data na delete
      await refreshLayerData();

      setDeleteLink(false);
      onCloseEditor();
    }
  };

  const setTarget = useCallback(
    async (linkTarget: number) => {
      if (restAuth) {
        setTargetInputLoading(true);

        if (selectedLink.type === "pano") {
          await mapPanoRepository.update(restAuth, { pano: linkTarget }, selectedLink.item);
        } else {
          await mapPoiRepository.update(restAuth, { poi: linkTarget }, selectedLink.item);
        }

        // Refresh all data na update
        await refreshLayerData();

        // Set new selected link to the updated target
        setSelectedLink({ type: selectedLink.type, item: linkTarget });
        setTargetInputLoading(false);
      }
    },
    [
      mapPanoRepository,
      mapPoiRepository,
      restAuth,
      selectedLink.item,
      selectedLink.type,
      setSelectedLink,
      refreshLayerData,
    ]
  );

  const setColor = useCallback(async (linkType: string) => {
    // if (target) {
    //   setColorInputLoading(true);
    //   const updateLinkData: {
    //     hotspot_type?: string;
    //     yaw?: number;
    //     pitch?: number;
    //     hotspot_coordinates?: [{ pitch: number; yaw: number }];
    //   } = {};
    //   updateLinkData.hotspot_type = linkType;
    //   if (selectedLink.type === "pano") {
    //     const links = await mapPanoRepository.update(
    //       restAuth,
    //       updateLinkData,
    //       selectedLink.item,
    //     );
    //     editData<"map">({ panorama_location: links });
    //   } else {
    //     const links = await mapPoiRepository.update(
    //       restAuth,
    //       updateLinkData,
    //       selectedLink.item,
    //     );
    //     editData<"map">({ poi_location: links });
    //   }
    //   setColorInputLoading(false);
    // }
  }, []);

  const setLat = useCallback(
    async (newLat: number) => {
      if (restAuth) {
        if (selectedLink.type === "pano") {
          await mapPanoRepository.update(restAuth, { lat: newLat }, selectedLink.item);
        } else {
          await mapPoiRepository.update(restAuth, { lat: newLat }, selectedLink.item);
        }

        // Refresh all data after coordinate update
        await refreshLayerData();
      }
    },
    [
      mapPanoRepository,
      mapPoiRepository,
      restAuth,
      selectedLink.item,
      selectedLink.type,
      refreshLayerData,
    ]
  );

  const setLong = useCallback(
    async (newLong: number) => {
      if (restAuth) {
        if (selectedLink.type === "pano") {
          await mapPanoRepository.update(restAuth, { long: newLong }, selectedLink.item);
        } else {
          await mapPoiRepository.update(restAuth, { long: newLong }, selectedLink.item);
        }

        // Refresh all data after coordinate update
        await refreshLayerData();
      }
    },
    [
      mapPanoRepository,
      mapPoiRepository,
      restAuth,
      selectedLink.item,
      selectedLink.type,
      refreshLayerData,
    ]
  );

  const setLayers = useCallback(
    async (newLayers: number[]) => {
      if (restAuth) {
        setLayersLoading(true);

        try {
          // Find layers to add and remove
          const layersToAdd = newLayers.filter((layerId) => !selectedLayers.includes(layerId));
          const layersToRemove = selectedLayers.filter((layerId) => !newLayers.includes(layerId));

          // Add point to new layers
          for (const layerId of layersToAdd) {
            if (selectedLink.type === "pano") {
              await mapPanoRepository.create(restAuth, { pano: selectedLink.item }, layerId);
            } else {
              await mapPoiRepository.create(restAuth, { poi: selectedLink.item }, layerId);
            }
          }

          // Remove point from old layers
          for (const layerId of layersToRemove) {
            if (selectedLink.type === "pano") {
              await mapPanoRepository.delete(restAuth, selectedLink.item, layerId);
            } else {
              await mapPoiRepository.delete(restAuth, selectedLink.item, layerId);
            }
          }

          // Refresh all data after layer changes
          await refreshLayerData();
        } finally {
          setLayersLoading(false);
        }
      }
    },
    [
      mapPanoRepository,
      mapPoiRepository,
      restAuth,
      selectedLink,
      selectedLayers,
      refreshLayerData,
      setLayersLoading,
    ]
  );

  const layerOptions = useMemo(() => {
    return state.map.layerGroups.map((layerGroup) => ({
      value: layerGroup.id,
      label: layerGroup.title,
    }));
  }, [state.map.layerGroups]);

  if (linkData === undefined) return null;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h1>
          <FormattedMessage
            id="editor.map.link.title"
            defaultMessage="Edit link"
            description="Text for map link editor title"
          />
        </h1>
        <IconButton style={{ position: "relative", top: "-9px" }} onClick={onCloseEditor}>
          <div>
            <CloseIcon />
          </div>
        </IconButton>
      </div>
      <InputGroup>
        <Label>
          <FormattedMessage
            id="editor.map.link.target"
            defaultMessage="Target"
            description="Text for map link target input label"
          />
        </Label>
        <SpinnerWrapper loading={targetInputLoading}>
          <div style={{ display: "flex", gap: "4px" }}>
            <Select
              value={selectedLink.item}
              onChange={(e: React.FormEvent<HTMLSelectElement>) => {
                setTarget(Number((e.target as HTMLSelectElement).value));
              }}
            >
              {(targets as (EditorMapPano | EditorMapPoi)[]).map(
                (linkTarget: EditorMapPano | EditorMapPoi, index: number) => (
                  <option
                    key={index}
                    value={"pano" in linkTarget ? linkTarget.pano : linkTarget.poi}
                  >
                    {linkTarget.title}
                  </option>
                )
              )}
            </Select>
            {selectedLink.type === "pano" && (linkData as EditorMapPano)?.pano && (
              <InlineInputButton
                onClick={() => {
                  const pano = state.panos.find(
                    (pano) => pano.id === (linkData as EditorMapPano)?.pano
                  );
                  if (pano) {
                    history.push(
                      "/editor/pano/" +
                        (linkData as EditorMapPano)?.pano +
                        "/" +
                        pano.scenarios[0].slug
                    );
                  }
                }}
              >
                <OpenIcon />
              </InlineInputButton>
            )}
          </div>
        </SpinnerWrapper>
      </InputGroup>
      <SpinnerWrapper loading={coordinateInputsLoading}>
        <InputGroup>
          <Label>
            <FormattedMessage
              id="editor.map.link.lat"
              defaultMessage="Latitude"
              description="Text for map link latitude input label"
            />
          </Label>
          <Input
            type="text"
            placeholder="Lat"
            value={lat}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value;
              if (/^[\d.-]*$/.test(value)) {
                setLatValue(value);
              }
            }}
            onBlur={() => {
              const formattedValue = lat.replace(",", "");
              if (/^-?\d*\.?\d*$/.test(formattedValue)) {
                setLat(parseFloat(formattedValue));
              } else {
                setLatValue(linkData.lat.toString() || "0");
              }
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter") {
                const formattedValue = lat.replace(",", "");
                if (/^-?\d*\.?\d*$/.test(formattedValue)) {
                  setLat(parseFloat(formattedValue));
                } else {
                  setLatValue(linkData.lat.toString());
                }
              }
            }}
          />
        </InputGroup>
        <InputGroup>
          <Label>
            <FormattedMessage
              id="editor.map.link.long"
              defaultMessage="Longitude"
              description="Text for map link longitude input label"
            />
          </Label>
          <Input
            type="text"
            placeholder="Long"
            value={long}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value;
              if (/^[\d.-]*$/.test(value)) {
                setLongValue(value);
              }
            }}
            onBlur={() => {
              const formattedValue = long.replace(",", "");
              if (/^-?\d*\.?\d*$/.test(formattedValue)) {
                setLong(parseFloat(formattedValue));
              } else {
                setLongValue(linkData.long.toString() || "");
              }
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter") {
                const formattedValue = long.replace(",", "");
                if (/^-?\d*\.?\d*$/.test(formattedValue)) {
                  setLong(parseFloat(formattedValue));
                } else {
                  setLongValue(linkData.long.toString() || "");
                }
              }
            }}
          />
        </InputGroup>
      </SpinnerWrapper>
      {!target && (
        <InputGroup>
          <Label>
            <FormattedMessage
              id="editor.map.link.layers"
              defaultMessage="Map layers"
              description="Text for map link layers selection label"
            />
          </Label>
          <SpinnerWrapper loading={layersLoading}>
            <MultiSelect
              options={layerOptions}
              selectedValues={selectedLayers}
              onChange={(values) => {
                setSelectedLayers(values);
                setLayers(values);
              }}
              placeholder={intl.formatMessage({
                id: "editor.map.link.layers.placeholder",
                defaultMessage: "Select map layers...",
                description: "Placeholder text for map link layers selection",
              })}
              disabled={layersLoading}
            />
          </SpinnerWrapper>
        </InputGroup>
      )}
      <div style={{ display: "flex", justifyContent: "center", marginTop: "16px" }}>
        <Button className="danger" onClick={() => setDeleteLink(true)}>
          <TrashIcon />
          <FormattedMessage
            id="editor.link.delete"
            defaultMessage="Delete link"
            description="Text for delete link button"
          />
        </Button>
      </div>
      <ConfirmDialog show={deleteLink} setShow={setDeleteLink} onConfirm={onDeleteLink}>
        <h2>
          <FormattedMessage
            id="editor.link.confirm_delete"
            defaultMessage="Are you sure you want to delete this link?"
            description="Confirmation message for deleting a link"
          />
        </h2>
      </ConfirmDialog>
    </>
  );
};

export default MapLinkEditor;
